import React, { useEffect } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useTranslation, Trans } from 'react-i18next';
import { RadioGroup, FormHelperText, Box, Accordion } from 'edenred-ui';
import { useFormContext } from 'react-hook-form';
import { useTheme } from 'styled-components';

import { FormFields } from '@epi-forms/helpers';
import { togglePrintInvoiceForPdf } from '@epi-actions/companyAccounts';
import { PaymentMethodProps } from '@epi-models/forms/PaymentMethod';
import { getUserCompany } from '@epi-actions/api';
import { getCustomerNumber } from '@epi-selectors/api';

import {
  PaymentMethods,
  getPaymentMinimalValue
} from '../../constants/paymentMethods';
import storage from '../../helpers/StorageHelper';

import { ExternalPayment } from './ExternalPayment/ExternalPayment';
import { ForwardInvoice } from './ForwardInvoice/ForwardInvoice';
import { DownloadInvoice } from './DownloadInvoice/DownloadInvoice';
import { PaymentMethodField } from './PaymentMethodField/PaymentMethodField';
import { PaymentInfo } from './PaymentInfo';
import { CreditCardMethod } from './CreditCardMethod';
import './PaymentMethod.scss';

function PaymentMethod({
  disabled,
  creditCardFeePercent = 0,
  onClickPaymentMethod,
  paymentMethods: { paymentMethods, isLoading, isError },
  isPaymentProcessing = false,
  disabledMethods = [],
  hiddenMethods = [],
  getPaymentMinValue,
  orderType,
  onPaymentMethodChanged = () => {},
  amountValue,
  isLoadCompanyAccountPage,
  defaultPaymentMethod,
  isMasterAccount
}: PaymentMethodProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const companyId = useSelector(getCustomerNumber);
  const {
    formState: { errors },
    watch,
    setValue
  } = useFormContext();

  const method = watch(FormFields.PaymentMethod);

  const creditCardMethods = paymentMethods.filter(
    t => t.type === PaymentMethods.CreditCard
  );

  const onlinePaymentMethods = paymentMethods.filter(
    t => t.type === PaymentMethods.Online
  );

  const isLessThanMinValue = (paymentMethod: string) => {
    return amountValue < getPaymentMinValue(paymentMethod);
  };

  useEffect(() => {
    storage.set('isPrintInvoicePdf', '');
    dispatch(togglePrintInvoiceForPdf(false));
    dispatch(getUserCompany());
  }, []);

  useEffect(() => {
    if (method === PaymentMethods.CreditCard && paymentMethods.length > 0) {
      setValue(FormFields.PaymentMethodCode, creditCardMethods[0].code);
    }
  }, [method]);

  return (
    <div className="PaymentMethod pl-25 py-0">
      <RadioGroup
        sx={
          errors.method && {
            '& .MuiFormControlLabel-label, span': {
              color: theme.warningColor
            }
          }
        }
      >
        {defaultPaymentMethod === PaymentMethods.EInvoice && (
          <Box display="flex" alignItems="center">
            <PaymentMethodField
              disabled={
                disabled ||
                disabledMethods.includes(
                  PaymentMethods.PrintInvoicePdfForTransferMoney
                )
              }
              label={t('forms.payment_method.einvoice')}
              name={FormFields.PaymentMethod}
              value={PaymentMethods.EInvoice}
              onChangeMethod={onPaymentMethodChanged}
            />
            <PaymentInfo title="forms.payment_method.einvoice_info" />
          </Box>
        )}
        {defaultPaymentMethod === PaymentMethods.Email && (
          <Box display="flex" alignItems="center">
            <PaymentMethodField
              disabled={
                disabled || disabledMethods.includes(PaymentMethods.PdfInvoice)
              }
              label={t('forms.payment_method.email_invoice')}
              name={FormFields.PaymentMethod}
              value={PaymentMethods.PdfInvoice}
              onChangeMethod={onPaymentMethodChanged}
            />
            <PaymentInfo title="forms.payment_method.email_invoice_info" />
          </Box>
        )}
        {!hiddenMethods.includes(PaymentMethods.CreditCard) && (
          <CreditCardMethod
            disabled={disabled}
            creditCardFeePercent={creditCardFeePercent}
            onClickPaymentMethod={onClickPaymentMethod}
            creditCardMethods={creditCardMethods}
            isLoading={isLoading}
            isError={isError}
            isPaymentProcessing={isPaymentProcessing}
            getPaymentMinValue={getPaymentMinValue}
            onPaymentMethodChanged={onPaymentMethodChanged}
            amountValue={amountValue}
            isMasterAccount={isMasterAccount}
          />
        )}
        {!hiddenMethods.includes(PaymentMethods.Online) && (
          <Box
            display="flex"
            alignItems="center"
            data-test="online-payment-method-checkbox"
          >
            <PaymentMethodField
              disabled={disabled}
              label={t('forms.payment_method.online_payment')}
              name={FormFields.PaymentMethod}
              value={PaymentMethods.Online}
              onChangeMethod={onPaymentMethodChanged}
            />
            <PaymentInfo title="forms.payment_method.invoicing_info" />
          </Box>
        )}
        <Accordion
          hideArrow
          summaryChild={<span />}
          expanded={
            method === PaymentMethods.Online && !isLoadCompanyAccountPage
          }
          detailsChild={
            <div
              className="online_payment"
              data-test="online-payment-method-accordion"
            >
              <ExternalPayment
                isError={isError || isLessThanMinValue(PaymentMethods.Online)}
                isLoading={isLoading}
                disabled={disabled}
                methods={onlinePaymentMethods}
                onClickPaymentMethod={onClickPaymentMethod}
                isProcessing={isPaymentProcessing}
              />
            </div>
          }
        />
        {!hiddenMethods.includes(PaymentMethods.LinkToOtherPerson) && (
          <PaymentMethodField
            disabled={
              disabled ||
              disabledMethods.includes(PaymentMethods.LinkToOtherPerson)
            }
            label={t('forms.payment_method.forward_invoice_by_mail')}
            name={FormFields.PaymentMethod}
            value={PaymentMethods.LinkToOtherPerson}
            onChangeMethod={onPaymentMethodChanged}
          />
        )}
        <Accordion
          hideArrow
          summaryChild={<span />}
          expanded={method === PaymentMethods.LinkToOtherPerson}
          detailsChild={
            method === PaymentMethods.LinkToOtherPerson && (
              <div data-test="forward-invoice-method-accordion">
                <ForwardInvoice orderType={orderType} />
              </div>
            )
          }
        />
        {!hiddenMethods.includes(PaymentMethods.PrintInvoicePdf) && (
          <PaymentMethodField
            disabled={
              disabled ||
              disabledMethods.includes(PaymentMethods.PrintInvoicePdf)
            }
            label={t('forms.payment_method.download_invoice_and_pay')}
            name={FormFields.PaymentMethod}
            value={PaymentMethods.PrintInvoicePdf}
            onChangeMethod={onPaymentMethodChanged}
          />
        )}
        <Accordion
          hideArrow
          summaryChild={<span />}
          expanded={method === PaymentMethods.PrintInvoicePdf}
          detailsChild={
            <div id="download-invoice-method-accordion">
              <DownloadInvoice orderType={orderType} />
            </div>
          }
        />
        <Accordion
          hideArrow
          summaryChild={<span />}
          expanded={method === PaymentMethods.PrintInvoicePdfForTransferMoney}
          detailsChild={
            <Box fontSize={16} ml={3.5} color={theme.gray70} mb={1}>
              <Trans
                defaults={t(
                  'forms.payment_method.download_invoice_as_pdf_for_transfer_money'
                )}
                components={{ break: <br />, bold: <b /> }}
              />
            </Box>
          }
        />
        <FormHelperText
          sx={{ fontSize: theme.sizeExtraSmall }}
          error={!!errors.method}
        >
          {errors?.method?.message as string}
        </FormHelperText>
      </RadioGroup>
    </div>
  );
}

const mapStateToProps = state => ({
  getPaymentMinValue: type => getPaymentMinimalValue(type)(state)
});

const PaymentMethodWithI18n = connect(mapStateToProps)(PaymentMethod);

export { PaymentMethodWithI18n as PaymentMethod };
